import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useRef } from "react";

export const useChatBotResponse = (chatbot_api_url) => {
  return useMutation({
    mutationFn: async (messages) => {
      const apiUrl = `${chatbot_api_url}/api/chat`;
      const requestBody = {
        messages,
        stream: false,
      };

      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        return response.data.message;
      } else {
        return {
          role: "assistant",
          content: "Error: Unable to fetch response from the server.",
        };
      }
    },
    onError: () => {
      console.error("Error in useChatBotResponse");
    },
  });
};

export const useGetChatBotInitMessage = (chatbot_api_url) => {
  return useQuery({
    queryKey: ["chatbot", "initMessage"],
    queryFn: async () => {
      try {
        const apiUrl = `${chatbot_api_url}/api/info`;
        const response = await axios.get(apiUrl, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          return response.data.chatbotDescription;
        }
      } catch (error) {
        console.error("Failed to fetch init message:", error);
      }
      return "Hi, I’m Leafie! \n I’m an experimental AI agent for eLTER users and researchers, here to help you understand eLTER and explore its research infrastructure sites, services, and datasets for long-term ecosystem research. \n \n Want to chat more? You can also find me at [leafie.dev.elter-ri.eu](https://leafie.dev.elter-ri.eu).";
    },
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useChatBotStreamResponse = (chatbot_api_url, onDelta) => {
  const abortControllerRef = useRef(null);

  const mutation = useMutation({
    mutationFn: async (messages) => {
      const apiUrl = `${chatbot_api_url}/api/chat/stream`;
      abortControllerRef.current = new AbortController();

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ messages }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error("Stream error");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullMessage = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const json = JSON.parse(line);
            if (json.delta) {
              fullMessage += json.delta;
              onDelta(json.delta, fullMessage);
            }
          } catch (e) {
            console.log(e);
          }
        }
      }

      return {
        role: "assistant",
        content: fullMessage,
      };
    },
    onError: () => {
      console.error("Error in useChatBotStreamResponse");
    },
  });

  const cancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  return { ...mutation, cancel };
};
